<div class="profile-page">
  <div class="profile-main">
    @if (displayLoading) {
      <div
        class="d-flex flex-column align-items-center justify-content-center common-loading-container"
      >
        <div class="w-25 text-center">
          <img class="common-loading" src="assets/menu_loading.gif" alt="common loading" />
          <p class="common-loading-text">Loading restaurant profile, please wait...</p>
        </div>
      </div>
    } @else {
      <form id="section-profile" class="section-card" autocomplete="off" [formGroup]="profileForm">
        <div class="card-heading">
          <span class="card-title">Restaurant Profile</span>
          <span class="card-subtitle">
            Manage your brand presentation, cover images, logo, and core contact details for
            customers.
          </span>
        </div>

        <div class="media-row">
          <div class="media-col cover-col">
            <span class="media-label">Cover Image</span>
            <span class="media-hint">Recommended aspect ratio: 16:5 (e.g. 1920x600px)</span>
            <div class="cover-preview-container" id="previewContainer">
              @if (coverImagePreview) {
                <img
                  [src]="coverImagePreview"
                  alt="Profile Preview"
                  (error)="onCoverImageError($event)"
                />
              } @else {
                <img
                  class="menu_loading bg-white"
                  src="assets/default/cover.png"
                  alt="Profile Preview"
                />
              }
            </div>
            <input
              type="file"
              #coverFileInput
              id="coverFileInput"
              class="file-input"
              accept="image/*"
              (change)="onCoverFileSelected($event)"
            />
            <div class="media-actions">
              <span class="media-caption">Live cover photo</span>
              <button type="button" class="link-remove" (click)="resetCoverToDefault()">
                Remove
              </button>
              <label type="button" for="coverFileInput" class="outline-btn">Upload Cover</label>
            </div>
            @if (coverImageSizeError) {
              <div class="d-flex mt-2 errorText gap-2">
                <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
                <p>{{ coverImageSizeError }}</p>
              </div>
            }
          </div>

          <div class="media-divider"></div>

          <div class="media-col logo-col">
            <span class="media-label">Logo</span>
            <span class="media-hint">Recommended size: square (512x512px)</span>
            <div class="logo-block">
              <div class="preview-container" id="previewContainer">
                @if (imagePreview) {
                  <img [src]="imagePreview" alt="Profile Preview" (error)="onImageError($event)" />
                } @else {
                  <img
                    class="menu_loading bg-white"
                    src="assets/default/logo.png"
                    alt="Profile Preview"
                  />
                }
              </div>
              <div class="logo-meta">
                <span class="brand-pill">Active Brand</span>
                <span class="media-hint">Max 10MB</span>
              </div>
            </div>
            <input
              type="file"
              #fileInput
              id="fileInput"
              class="file-input"
              accept="image/*"
              (change)="onFileSelected($event)"
            />
            <div class="media-actions">
              <span class="media-caption">App identity logo</span>
              <button type="button" class="link-remove" (click)="resetLogoToDefault()">
                Remove
              </button>
              <label type="button" for="fileInput" class="outline-btn">Upload Logo</label>
            </div>
            @if (imageSizeError) {
              <div class="d-flex mt-2 errorText gap-2">
                <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
                <p>{{ imageSizeError }}</p>
              </div>
            }
          </div>
        </div>

        <div class="field-grid">
          <div class="field">
            <label class="field-label">Restaurant Name</label>
            <input type="text" class="field-input" formControlName="restaurant_name" />
            @if (
              profileForm.get('restaurant_name')?.errors?.['required'] &&
              (profileForm.get('restaurant_name')?.touched || profileForm.get('first_name')?.dirty)
            ) {
              <div class="d-flex mt-2 errorText gap-2">
                <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
                <p>Restaurant Name cannot be empty.</p>
              </div>
            } @else if (
              profileForm.get('restaurant_name')?.errors?.['minlength'] &&
              (profileForm.get('restaurant_name')?.touched || profileForm.get('first_name')?.dirty)
            ) {
              <div class="d-flex mt-2 errorText gap-2">
                <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
                <p>Restaurant Name must be at least 3 characters.</p>
              </div>
            }
          </div>
          <div class="field">
            <label class="field-label">Address</label>
            <input
              type="text"
              class="field-input"
              formControlName="address"
              [ngClass]="{
                'input-error':
                  profileForm.get('display_name')?.invalid &&
                  (profileForm.get('display_name')?.touched ||
                    profileForm.get('display_name')?.dirty),
              }"
            />
            @if (
              profileForm.get('address')?.errors?.['required'] &&
              (profileForm.get('address')?.touched || profileForm.get('display_name')?.dirty)
            ) {
              <div class="d-flex mt-2 errorText gap-2">
                <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
                <p>Address cannot be empty.</p>
              </div>
            } @else if (
              profileForm.get('address')?.errors?.['minlength'] &&
              (profileForm.get('address')?.touched || profileForm.get('display_name')?.dirty)
            ) {
              <div class="d-flex mt-2 errorText gap-2">
                <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
                <p>Address must be at least 3 characters.</p>
              </div>
            }
          </div>
          <div class="field">
            <label class="field-label">Time Zone</label>
            <input type="text" class="field-input" formControlName="time_zone" />
          </div>
          <div class="field">
            <label class="field-label">Email Address</label>
            <input type="email" class="field-input" formControlName="email" />
            @if (
              profileForm.get('email')?.errors?.['required'] &&
              (profileForm.get('email')?.touched || profileForm.get('email')?.dirty)
            ) {
              <div class="d-flex mt-2 errorText gap-2">
                <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
                <p>Email cannot be empty.</p>
              </div>
            } @else if (
              profileForm.get('email')?.errors?.['pattern'] &&
              (profileForm.get('email')?.touched || profileForm.get('email')?.dirty)
            ) {
              <div class="d-flex mt-2 errorText gap-2">
                <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
                <p>Invalid email format.</p>
              </div>
            }
          </div>
          <div class="field">
            <label class="field-label" for="restaurantMobileNumber">Mobile Number</label>
            <div class="phone-field">
              <span class="phone-prefix" aria-label="United States">
                <span class="phone-flag" aria-hidden="true">🇺🇸</span>
                +1
              </span>
              <input
                id="restaurantMobileNumber"
                type="tel"
                class="field-input phone-input"
                formControlName="phone_number"
              />
            </div>
          </div>
        </div>

        <div class="card-actions">
          <button type="button" class="cancel-btn" (click)="resetForm()">Cancel</button>
          <button
            type="button"
            class="save-btn"
            [disabled]="profileForm.invalid || !hasProfileFormChanges()"
            (click)="onSubmit()"
          >
            Save Changes
          </button>
        </div>
      </form>

      <!-- Outside the hours form: a POS-managed restaurant can still close for now (FNF-1612) -->
      <app-restaurant-status id="section-status" class="section-card" />

      <form
        id="section-hours"
        class="section-card"
        [formGroup]="hoursForm"
        autocomplete="off"
        [ngClass]="{ 'disabled-content': isNotFnfHardware }"
      >
        <div class="row mt-3">
          <div class="col-8 d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center gap-2">
              <div class="clock-icon-circle">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z"
                    stroke="#6C7689"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M8 5V8L10 10"
                    stroke="#6C7689"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <div>
                <h3 class="restaurant-open-days-title">Restaurant Open Days</h3>
                <p class="restaurant-open-days-subtitle">
                  Set the usual opening hours (Default business hours)
                </p>
              </div>
            </div>
            <button
              type="button"
              class="add-schedule-btn"
              [disabled]="areAllDaysSelected() || !areAllSchedulesValid()"
              (click)="addSchedule()"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                class="me-1"
              >
                <path
                  d="M8 3V13M3 8H13"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              Add Schedule
            </button>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-8">
            @for (schedule of schedules.controls; track $index; let scheduleIndex = $index) {
              <div class="schedule-container mb-5" [formGroup]="getSchedule(scheduleIndex)">
                @if (scheduleIndex > 0) {
                  <button
                    type="button"
                    class="btn-remove-schedule bg-dark rounded-4"
                    (click)="removeSchedule(scheduleIndex)"
                  >
                    <span class="bx bx-trash text-white"></span>
                  </button>
                }
                <!-- Day Selection -->
                <div class="day-selection-container justify-content-between" formGroupName="days">
                  @for (day of daysOfWeek; track day) {
                    <label
                      class="day-checkbox-label rounded-3 p-2 pl-3 pr-3"
                      [class.disabled]="isDayDisabled(scheduleIndex, day)"
                    >
                      <input
                        type="checkbox"
                        [formControlName]="day"
                        class="day-checkbox"
                        [disabled]="isDayDisabled(scheduleIndex, day)"
                      />
                      <span
                        class="day-checkbox-custom"
                        [class.checked]="isDaySelected(scheduleIndex, day)"
                        [class.disabled]="isDayDisabled(scheduleIndex, day)"
                      >
                        @if (isDaySelected(scheduleIndex, day)) {
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                          >
                            <path
                              d="M10 3L4.5 8.5L2 6"
                              stroke="white"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        }
                      </span>
                      <span
                        class="day-label"
                        [class.disabled]="isDayDisabled(scheduleIndex, day)"
                        >{{ day }}</span
                      >
                    </label>
                  }
                </div>

                <!-- Time Slots -->
                <div class="time-slots-container">
                  <div class="add-time-slot-btn-container">
                    <button
                      type="button"
                      class="btn-add-time-slot mt-4"
                      [disabled]="!canAddTimeSlot(scheduleIndex)"
                      (click)="addTimeSlot(scheduleIndex)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M8 3V13M3 8H13"
                          stroke="#6C7689"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                  <div class="time-slots-list" formArrayName="timeSlots">
                    @for (
                      timeSlot of getTimeSlots(scheduleIndex).controls;
                      track $index;
                      let timeSlotIndex = $index
                    ) {
                      <div
                        class="time-slot-item"
                        [formGroup]="getTimeSlot(scheduleIndex, timeSlotIndex)"
                      >
                        @if (timeSlotIndex > 0) {
                          <button
                            type="button"
                            class="btn-remove-time-slot ml-n5 mr-1 text-black-50"
                            (click)="removeTimeSlot(scheduleIndex, timeSlotIndex)"
                          >
                            <span class="bx bx-trash"></span>
                          </button>
                        }
                        <div class="time-input-group">
                          <div class="time-input-field">
                            <label class="time-label">Open Time</label>
                            <div class="time-input-wrapper">
                              @if (
                                getSelectedTime(scheduleIndex, timeSlotIndex, 'open') !==
                                'Select time'
                              ) {
                                <button
                                  class="btn dropdown-toggle bg-white form-control border text-left rounded-2"
                                  [ngClass]="{
                                    'border-danger':
                                      hasTimeSlotError(
                                        scheduleIndex,
                                        timeSlotIndex,
                                        'timeRangeInvalid'
                                      ) ||
                                      hasTimeSlotError(
                                        scheduleIndex,
                                        timeSlotIndex,
                                        'duplicateTimeSlot'
                                      ) ||
                                      hasTimeSlotError(
                                        scheduleIndex,
                                        timeSlotIndex,
                                        'sequenceInvalid',
                                        'open'
                                      ) ||
                                      (getTimeSlot(scheduleIndex, timeSlotIndex)
                                        .get('openTime')
                                        ?.hasError('required') &&
                                        (getTimeSlot(scheduleIndex, timeSlotIndex).get('openTime')
                                          ?.dirty ||
                                          getTimeSlot(scheduleIndex, timeSlotIndex).get('openTime')
                                            ?.touched)),
                                    disabled: !hasAnyDaySelected(scheduleIndex),
                                  }"
                                  type="button"
                                  [id]="'openTimeDropdown_' + scheduleIndex + '_' + timeSlotIndex"
                                  [attr.data-bs-toggle]="
                                    hasAnyDaySelected(scheduleIndex) ? 'dropdown' : null
                                  "
                                  [disabled]="!hasAnyDaySelected(scheduleIndex)"
                                  aria-expanded="false"
                                >
                                  {{ getSelectedTime(scheduleIndex, timeSlotIndex, 'open') }}
                                </button>
                              } @else {
                                <button
                                  class="btn dropdown-toggle bg-white form-control border text-left rounded-2 text-black-50"
                                  [ngClass]="{
                                    'border-danger':
                                      hasTimeSlotError(
                                        scheduleIndex,
                                        timeSlotIndex,
                                        'timeRangeInvalid'
                                      ) ||
                                      hasTimeSlotError(
                                        scheduleIndex,
                                        timeSlotIndex,
                                        'duplicateTimeSlot'
                                      ) ||
                                      hasTimeSlotError(
                                        scheduleIndex,
                                        timeSlotIndex,
                                        'sequenceInvalid',
                                        'open'
                                      ) ||
                                      (getTimeSlot(scheduleIndex, timeSlotIndex)
                                        .get('openTime')
                                        ?.hasError('required') &&
                                        (getTimeSlot(scheduleIndex, timeSlotIndex).get('openTime')
                                          ?.dirty ||
                                          getTimeSlot(scheduleIndex, timeSlotIndex).get('openTime')
                                            ?.touched)),
                                    disabled: !hasAnyDaySelected(scheduleIndex),
                                  }"
                                  type="button"
                                  [id]="'openTimeDropdown_' + scheduleIndex + '_' + timeSlotIndex"
                                  [attr.data-bs-toggle]="
                                    hasAnyDaySelected(scheduleIndex) ? 'dropdown' : null
                                  "
                                  [disabled]="!hasAnyDaySelected(scheduleIndex)"
                                  aria-expanded="false"
                                >
                                  Select time
                                </button>
                              }
                              <ul
                                class="dropdown-menu"
                                [attr.aria-labelledby]="
                                  'openTimeDropdown_' + scheduleIndex + '_' + timeSlotIndex
                                "
                              >
                                @for (timeOption of timeOptionsDisplay; track timeOption.value) {
                                  <li
                                    (click)="
                                      changeTime(
                                        scheduleIndex,
                                        timeSlotIndex,
                                        'open',
                                        timeOption.value
                                      )
                                    "
                                  >
                                    <a class="dropdown-item">{{ timeOption.display }}</a>
                                  </li>
                                }
                              </ul>
                            </div>
                          </div>
                          <div class="time-input-field">
                            <label class="time-label">Close Time</label>
                            <div class="time-input-wrapper">
                              @if (
                                getSelectedTime(scheduleIndex, timeSlotIndex, 'close') !==
                                'Select time'
                              ) {
                                <button
                                  class="btn dropdown-toggle bg-white form-control border text-left rounded-2"
                                  [ngClass]="{
                                    'border-danger':
                                      hasTimeSlotError(
                                        scheduleIndex,
                                        timeSlotIndex,
                                        'timeRangeInvalid'
                                      ) ||
                                      hasTimeSlotError(
                                        scheduleIndex,
                                        timeSlotIndex,
                                        'duplicateTimeSlot'
                                      ) ||
                                      hasTimeSlotError(
                                        scheduleIndex,
                                        timeSlotIndex,
                                        'sequenceInvalid',
                                        'close'
                                      ) ||
                                      (getTimeSlot(scheduleIndex, timeSlotIndex)
                                        .get('closeTime')
                                        ?.hasError('required') &&
                                        (getTimeSlot(scheduleIndex, timeSlotIndex).get('closeTime')
                                          ?.dirty ||
                                          getTimeSlot(scheduleIndex, timeSlotIndex).get('closeTime')
                                            ?.touched)),
                                    disabled:
                                      !hasAnyDaySelected(scheduleIndex) ||
                                      !canSelectCloseTime(scheduleIndex, timeSlotIndex),
                                  }"
                                  type="button"
                                  [id]="'closeTimeDropdown_' + scheduleIndex + '_' + timeSlotIndex"
                                  [attr.data-bs-toggle]="
                                    hasAnyDaySelected(scheduleIndex) &&
                                    canSelectCloseTime(scheduleIndex, timeSlotIndex)
                                      ? 'dropdown'
                                      : null
                                  "
                                  [disabled]="
                                    !hasAnyDaySelected(scheduleIndex) ||
                                    !canSelectCloseTime(scheduleIndex, timeSlotIndex)
                                  "
                                  aria-expanded="false"
                                >
                                  {{ getSelectedTime(scheduleIndex, timeSlotIndex, 'close') }}
                                </button>
                              } @else {
                                <button
                                  class="btn dropdown-toggle bg-white form-control border text-left rounded-2 text-black-50"
                                  [ngClass]="{
                                    'border-danger':
                                      hasTimeSlotError(
                                        scheduleIndex,
                                        timeSlotIndex,
                                        'timeRangeInvalid'
                                      ) ||
                                      hasTimeSlotError(
                                        scheduleIndex,
                                        timeSlotIndex,
                                        'duplicateTimeSlot'
                                      ) ||
                                      hasTimeSlotError(
                                        scheduleIndex,
                                        timeSlotIndex,
                                        'sequenceInvalid',
                                        'close'
                                      ) ||
                                      (getTimeSlot(scheduleIndex, timeSlotIndex)
                                        .get('closeTime')
                                        ?.hasError('required') &&
                                        (getTimeSlot(scheduleIndex, timeSlotIndex).get('closeTime')
                                          ?.dirty ||
                                          getTimeSlot(scheduleIndex, timeSlotIndex).get('closeTime')
                                            ?.touched)),
                                    disabled:
                                      !hasAnyDaySelected(scheduleIndex) ||
                                      !canSelectCloseTime(scheduleIndex, timeSlotIndex),
                                  }"
                                  type="button"
                                  [id]="'closeTimeDropdown_' + scheduleIndex + '_' + timeSlotIndex"
                                  [attr.data-bs-toggle]="
                                    hasAnyDaySelected(scheduleIndex) &&
                                    canSelectCloseTime(scheduleIndex, timeSlotIndex)
                                      ? 'dropdown'
                                      : null
                                  "
                                  [disabled]="
                                    !hasAnyDaySelected(scheduleIndex) ||
                                    !canSelectCloseTime(scheduleIndex, timeSlotIndex)
                                  "
                                  aria-expanded="false"
                                >
                                  Select time
                                </button>
                              }
                              <ul
                                class="dropdown-menu"
                                [attr.aria-labelledby]="
                                  'closeTimeDropdown_' + scheduleIndex + '_' + timeSlotIndex
                                "
                              >
                                @for (
                                  timeOption of getCloseTimeOptionsDisplay(
                                    scheduleIndex,
                                    timeSlotIndex
                                  );
                                  track timeOption.value
                                ) {
                                  <li
                                    (click)="
                                      changeTime(
                                        scheduleIndex,
                                        timeSlotIndex,
                                        'close',
                                        timeOption.value
                                      )
                                    "
                                  >
                                    <a class="dropdown-item">{{ timeOption.display }}</a>
                                  </li>
                                }
                              </ul>
                            </div>
                            @if (isNextDayClose(scheduleIndex, timeSlotIndex)) {
                              <span class="next-day-label">Next day</span>
                            }
                          </div>
                        </div>
                        <!-- @if (hasTimeSlotError(scheduleIndex, timeSlotIndex, 'timeRangeInvalid') || getTimeSlot(scheduleIndex, timeSlotIndex).get('openTime')?.hasError('required') || getTimeSlot(scheduleIndex, timeSlotIndex).get('closeTime')?.hasError('required')) {
                                                    <div class="d-flex mt-2 errorText gap-2">
                                                        <svg class="warning-icon icon-danger" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                            <path d="M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM7 4H9V6H7V4ZM7 8H9V12H7V8Z" fill="currentColor"/>
                                                        </svg>
                                                        <p>{{ getTimeSlotErrorMessage(scheduleIndex, timeSlotIndex) }}</p>
                                                    </div>
                                                } -->
                      </div>
                      @if (shouldDisplayTimeSlotError(scheduleIndex, timeSlotIndex)) {
                        <div class="d-flex mt-2 errorText gap-2">
                          <svg
                            class="warning-icon icon-danger"
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM7 4H9V6H7V4ZM7 8H9V12H7V8Z"
                              fill="currentColor"
                            />
                          </svg>
                          <p>{{ getTimeSlotErrorMessage(scheduleIndex, timeSlotIndex) }}</p>
                        </div>
                      }
                    }
                  </div>
                </div>
              </div>
            }
            @if (schedules.length === 0) {
              <div class="no-schedules-message">
                <p>No schedules added yet. Click "Add Schedule" to create your first schedule.</p>
              </div>
            }
          </div>
        </div>
        <div class="card-actions">
          <button type="button" class="cancel-btn" (click)="resetHoursForm()">Cancel</button>
          <button
            type="button"
            class="save-btn"
            [disabled]="hoursForm.invalid || !hasHoursFormChanges()"
            (click)="saveRestaurantHours()"
          >
            {{ hasServingHours() ? 'Update Changes' : 'Save Changes' }}
          </button>
        </div>
      </form>

      @if (canEditClosedDates) {
        <!-- Outside the hours form, like the status card: a POS-managed
             restaurant still sets its own holidays (FNF-1612). -->
        <section id="section-closed-dates" class="section-card" data-testid="closed-dates">
          <div class="d-flex align-items-center gap-2">
            <div class="clock-icon-circle">
              <i class="bx bx-calendar-x" aria-hidden="true"></i>
            </div>
            <div>
              <h3 class="restaurant-open-days-title">Holidays &amp; closed dates</h3>
              <p class="restaurant-open-days-subtitle">
                The same dates as Reservation settings → Closed dates
              </p>
            </div>
          </div>
          @if (closedDatesState === 'loading') {
            <p class="restaurant-open-days-subtitle">Loading the closed dates…</p>
          } @else if (closedDatesState === 'failed') {
            <p class="restaurant-open-days-subtitle" role="alert">
              Closed dates couldn't be loaded. Reload the page to try again.
            </p>
          } @else {
            <app-date-exceptions
              [exceptions]="dateExceptions"
              (changed)="dateExceptions = $event.date_exceptions ?? []"
            />
          }
        </section>
      }
    }
  </div>

  <aside class="page-rail">
    <span class="rail-title">On this page</span>
    <button
      type="button"
      class="rail-link"
      [class.active]="selectedProfile == 'profile'"
      (click)="scrollToSection('profile')"
    >
      Restaurant Profile
    </button>
    <button
      type="button"
      class="rail-link"
      [class.active]="selectedProfile == 'status'"
      (click)="scrollToSection('status')"
    >
      Restaurant Status
    </button>
    <button
      type="button"
      class="rail-link"
      [class.active]="selectedProfile == 'hours'"
      (click)="scrollToSection('hours')"
    >
      Restaurant Hours
    </button>
    @if (canEditClosedDates) {
      <button
        type="button"
        class="rail-link"
        [class.active]="selectedProfile == 'closed-dates'"
        (click)="scrollToSection('closed-dates')"
      >
        Holidays &amp; Closed Dates
      </button>
    }
  </aside>
</div>
